import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AuthUser, BackendUserData } from '@features/auth/schema';
import type { RootState } from '@store/index';

/**
 * Admin-panel role codes (from `master.role.code`).
 *
 * `user` is intentionally omitted — the mobile-app role has no admin panel
 * access, and the backend rejects its tokens at `resolveBearer`. Surfacing
 * it in the type would invite the wrong UI checks.
 */
export type AdminRoleCode = 'admin' | 'tech_team' | 'business_team';

interface AuthState {
  user: AuthUser | null;
  profile: BackendUserData | null;
  token: string | null;
  /** Loaded from `GET /me` on the commodity-mdm-api after every fresh session. */
  roles: AdminRoleCode[];
  initializing: boolean;
}

const initialState: AuthState = {
  user: null,
  profile: null,
  token: null,
  roles: [],
  initializing: true,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setSession: (
      state,
      action: PayloadAction<{ user: AuthUser; token: string; profile?: BackendUserData }>,
    ) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      if (action.payload.profile) state.profile = action.payload.profile;
    },
    setRoles: (state, action: PayloadAction<AdminRoleCode[]>) => {
      state.roles = action.payload;
    },
    clearSession: (state) => {
      state.user = null;
      state.profile = null;
      state.token = null;
      state.roles = [];
    },
    setInitializing: (state, action: PayloadAction<boolean>) => {
      state.initializing = action.payload;
    },
  },
});

export const authActions = authSlice.actions;
export const authReducer = authSlice.reducer;

export const selectAuth = (s: RootState) => s.auth;
export const selectAuthUser = (s: RootState) => s.auth.user;
export const selectAuthProfile = (s: RootState) => s.auth.profile;
export const selectAuthRoles = (s: RootState) => s.auth.roles;
export const selectIsAuthenticated = (s: RootState) =>
  Boolean(s.auth.user && s.auth.token);

/** ANY-of role check: returns true if the user holds at least one of `allowed`. */
export const hasAnyRole = (
  userRoles: readonly AdminRoleCode[],
  allowed: readonly AdminRoleCode[],
): boolean => userRoles.some((r) => allowed.includes(r));
